import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartBarIcon, 
  CurrencyRupeeIcon, 
  UserGroupIcon, 
  ClipboardDocumentCheckIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';

interface ReportCard {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  link: string;
  permission: string; // Changed from roles: string[]
}

const ReportDashboard: React.FC = () => {
  const { can } = useAuth(); // Changed from isAllowed to can
  
  const reportCards: ReportCard[] = [
    {
      id: 'financial',
      title: 'Финансовые отчеты',
      description: 'Доходы, расходы, прибыльность, денежные потоки',
      icon: <CurrencyRupeeIcon className="h-10 w-10 text-emerald-600" />,
      link: '/reports/financial',
      permission: 'reports.financial' // Example permission key
    },
    {
      id: 'structures',
      title: 'Отчеты по конструкциям',
      description: 'Занятость, популярность, доходность',
      icon: <BuildingOfficeIcon className="h-10 w-10 text-blue-600" />,
      link: '/reports/structures',
      permission: 'reports.structures' // Example permission key
    },
    {
      id: 'clients',
      title: 'Отчеты по клиентам',
      description: 'Активность, заказы, финансы клиентов',
      icon: <UserGroupIcon className="h-10 w-10 text-indigo-600" />,
      link: '/reports/clients',
      permission: 'reports.clients' // Example permission key
    },
    {
      id: 'orders',
      title: 'Отчеты по заказам',
      description: 'Статусы заказов, финансовые показатели',
      icon: <DocumentTextIcon className="h-10 w-10 text-orange-600" />,
      link: '/reports/orders',
      permission: 'reports.orders' // Example permission key
    },
    {
      id: 'workorders',
      title: 'Отчеты по нарядам',
      description: 'Статистика выполнения работ',
      icon: <ClipboardDocumentCheckIcon className="h-10 w-10 text-red-600" />,
      link: '/reports/workorders',
      permission: 'reports.work_orders' // Example permission key
    },
    {
      id: 'expenses',
      title: 'Отчеты по расходам',
      description: 'Анализ расходов по категориям',
      icon: <ChartBarIcon className="h-10 w-10 text-purple-600" />,
      link: '/reports/expenses',
      permission: 'reports.expenses' // Example permission key
    },
    {
      id: 'analytics',
      title: 'Аналитические отчеты',
      description: 'Сложные аналитические отчеты и прогнозы',
      icon: <ArrowTrendingUpIcon className="h-10 w-10 text-cyan-600" />,
      link: '/reports/analytics',
      permission: 'reports.analytics' // Example permission key
    }
  ];
  
  // Filter cards based on user permission
  const accessibleReports = reportCards.filter(card =>
    can(card.permission) // Changed from isAllowed(card.roles)
  );
  
  return (
    <div>
      <PageHeader
        title="Отчеты"
        subtitle="Доступ к различным типам отчетов и аналитике"
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {accessibleReports.map((card) => (
          <Link
            key={card.id}
            to={card.link}
            className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow duration-200 flex flex-col h-full"
          >
            <div className="mb-4">{card.icon}</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{card.title}</h3>
            <p className="text-gray-600 mb-4 flex-grow">{card.description}</p>
            <div className="text-primary font-medium">Просмотреть отчеты</div>
          </Link>
        ))}
      </div>
      
      {accessibleReports.length === 0 && (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-gray-500">
            У вас нет доступа к отчетам. Обратитесь к администратору.
          </p>
        </div>
      )}
    </div>
  );
};

export default ReportDashboard;
